// Customize-mode tool group (Figma "Icon Button (Domain)" group 6350:134419):
// a segmented DS AppIconButtonGroup whose segments each open a dropdown — the
// gear opens the Metric picker, the rows icon opens the dimension picker, and
// the chevron opens the Chart-type picker (Figma 6350:135101).
//
// The dimension segment (DEV-2432 §5) holds BOTH variables of a Stacked bar in
// ONE menu: a pinned "Aggregate by" / "Stack by" tab pair on top, and the open
// tab decides which field the list below writes. A bar or a donut has one
// variable, so its menu takes the plain "Aggregate by" title instead. The same
// dimension can never sit in both slots: each tab hides the value the other
// slot holds (§5.5).
// Reordering is done by dragging the whole card, not here.
// Shared by chart panels and metric tiles.
import { useEffect, useMemo, useState } from 'react'
import { AppIconButtonGroup, Tabs, TabList, Tab } from '@clickguard/design-system'
import { Settings04, ChevronSelectorVertical, Rows03 } from './icons'
import { IconMenu, type IconMenuSection } from './icon-menu'
import { BAR_ONLY_METRICS } from '../store/use-report-layout-store'
import { useDimensionCatalog } from '../data/use-analytics-data'

// Chart-type options: a plain bar, a stacked bar (its own type — the customize
// drawer's "Stretch to 100%" toggle normalizes it), and a pie.
export const CHART_TYPES = ['Bar', 'Stacked bar', 'Pie']

// Human-readable labels for the chart-type VALUES above. The stored value stays
// 'Pie' — the DS Gauge renderer and every `case 'Pie'` switch key on it — but we
// surface it as "Donut" (ticket §4.4: the label is "Donut" in both the on-card
// menu and the side panel — not "Pie", not "Donut chart"). Keeping value and
// label decoupled means renaming the copy never migrates a persisted layout (a
// saved 'Pie' still resolves).
export const CHART_TYPE_LABELS: Record<string, string> = {
	Bar: 'Bar',
	'Stacked bar': 'Stacked bar',
	Pie: 'Donut'
}
export const chartTypeLabel = (t: string) => CHART_TYPE_LABELS[t] ?? t

// Chart types a given metric may use. BAR_ONLY_METRICS (a rate, a unique count)
// are Bar-only per the §4.6 matrix — a share-of-total donut/stacked reads wrong
// for them. Shared by the on-card picker and the customize drawer so both offer
// the same set.
export const chartTypesFor = (metric?: string) =>
	metric && BAR_ONLY_METRICS.has(metric) ? ['Bar'] : CHART_TYPES

// The field names of a Stacked bar's two variables (ticket §2.5 / §5).
export const AGGREGATE_BY_LABEL = 'Aggregate by'
export const STACK_BY_LABEL = 'Stack by'

// Metric options (match the Key-metrics tile labels so tile selection lines up).
export const METRIC_NAMES = [
	'CTR',
	'Bounce rate',
	'Engaged clicks',
	'Total clicks',
	'Avg time on site'
]

// The DS `Tabs` ships two sizes only: `md` (16px) and `sm` (14px). Inside a
// 240px dropdown the 14px tab matched the DS ListItem rows below it, so the
// header competed with its own options. 12px is the size the plain `title`
// header uses in the Metric and Chart-type menus, so all three headers read
// alike. Inline `style`, not a className: the DS `cg-` prefix breaks twMerge
// dedupe, so a product `text-*` class does NOT reliably beat `cg-text-text-sm`.
const TAB_TYPE = { fontSize: 12, lineHeight: '18px' }

// The dimension segment. Its own component so only the panels that show
// dimensions subscribe to the catalog query — metric tiles never do.
const DimensionMenu = ({
	stacked,
	dimension,
	dimension2,
	onSelectDimension,
	onSelectDimension2
}: {
	stacked: boolean
	dimension?: string
	dimension2?: string
	onSelectDimension?: (dimension: string) => void
	onSelectDimension2?: (dimension2: string) => void
}) => {
	// Which variable the option list writes. It stays put between opens, so two
	// changes to Stack by in a row do not send the user back to Aggregate by.
	const [dimSlot, setDimSlot] = useState<1 | 2>(1)
	useEffect(() => {
		if (!stacked) setDimSlot(1)
	}, [stacked])
	const slot = stacked ? dimSlot : 1
	// Server-driven dimension list (labels + availability) with the static
	// registry as its synchronous fallback — the same catalog the table picker
	// offers, so the two can never drift apart.
	const { categories } = useDimensionCatalog()
	// The open tab's list: same taxonomy for both variables, minus the value the
	// OTHER slot holds, so the same dimension cannot be chosen twice (§5.5).
	const sections = useMemo<IconMenuSection[]>(() => {
		const current = slot === 2 ? dimension2 : dimension
		const taken = slot === 2 ? dimension : stacked ? dimension2 : undefined
		const pick = slot === 2 ? onSelectDimension2 : onSelectDimension
		return categories
			.map((cat) => ({
				title: cat.label,
				items: cat.dimensions
					.filter((d) => !taken || d.label !== taken || d.label === current)
					.map((d) => ({
						label: d.label,
						selected: d.label === current,
						onClick: pick ? () => pick(d.label) : undefined
					}))
			}))
			.filter((sec) => sec.items.length > 0)
	}, [categories, slot, stacked, dimension, dimension2, onSelectDimension, onSelectDimension2])
	// Aggregate by / Stack by are real tabs: the DS `Tabs` at `size="sm"`, with
	// `fullBleed` carrying the baseline across the row so the tab strip IS the
	// header divider and IconMenu draws none of its own.
	const header = stacked ? (
		<Tabs size="sm" value={String(slot)} onChange={(v) => setDimSlot(v === '2' ? 2 : 1)}>
			<TabList fullBleed>
				<Tab value="1" label={AGGREGATE_BY_LABEL} style={TAB_TYPE} />
				<Tab value="2" label={STACK_BY_LABEL} style={TAB_TYPE} />
			</TabList>
		</Tabs>
	) : undefined
	return (
		<IconMenu
			ariaLabel="Dimension"
			title={AGGREGATE_BY_LABEL}
			header={header}
			contentKey={`dim${slot}`}
			icon={<Rows03 />}
			sections={sections}
		/>
	)
}

export const SlotToolGroup = ({
	selectedChartType,
	selectedMetric,
	selectedDimension,
	selectedDimension2,
	showChartType = true,
	showDimensions = false,
	metricOptions = METRIC_NAMES,
	metricGroups,
	onSelectMetric,
	onSelectChartType,
	onSelectDimension,
	onSelectDimension2
}: {
	selectedChartType?: string
	selectedMetric?: string
	/** The chart's primary breakdown dimension — "Aggregate by" on the card. */
	selectedDimension?: string
	/** The stack-by dimension — "Stack by", a Stacked bar only. */
	selectedDimension2?: string
	/** Whether to show the chart-type picker. Off for metric cards (no charts). */
	showChartType?: boolean
	/** Whether to show the dimension picker. Off for metric cards, which have no
	 *  breakdown. */
	showDimensions?: boolean
	/** Flat metric choices for this slot (defaults to the Key-metrics set). Used
	 *  when `metricGroups` is not supplied. */
	metricOptions?: string[]
	/** Grouped metric choices — when supplied the Metric menu renders labeled
	 *  sections instead of one flat list. Order here is the render order. */
	metricGroups?: { title?: string; options: string[] }[]
	/** Wire these so picking from the on-card menus actually mutates the slot's
	 *  config in the store — without them the menus open but do nothing. */
	onSelectMetric?: (metric: string) => void
	onSelectChartType?: (chartType: string) => void
	/** Wire these so the dimension menu mutates the slot's dimensions. Both take
	 *  the dimension LABEL (the shape the layout config persists). */
	onSelectDimension?: (dimension: string) => void
	onSelectDimension2?: (dimension2: string) => void
}) => {
	const toItem = (m: string) => ({
		label: m,
		selected: m === selectedMetric,
		onClick: onSelectMetric ? () => onSelectMetric(m) : undefined
	})
	return (
		<AppIconButtonGroup size="mini">
			<IconMenu
				ariaLabel="Metric"
				title="Metric"
				icon={<Settings04 />}
				{...(metricGroups
					? { sections: metricGroups.map((g) => ({ title: g.title, items: g.options.map(toItem) })) }
					: { items: metricOptions.map(toItem) })}
			/>
			{showDimensions && (
				<DimensionMenu
					// Only a stacked bar stacks by a SECOND dimension, so Stack by belongs
					// to that type alone. A bar or a donut has one breakdown.
					stacked={selectedChartType === 'Stacked bar'}
					dimension={selectedDimension}
					dimension2={selectedDimension2}
					onSelectDimension={onSelectDimension}
					onSelectDimension2={onSelectDimension2}
				/>
			)}
			{showChartType && (
				<IconMenu
					ariaLabel="Chart type"
					title="Chart type"
					icon={<ChevronSelectorVertical />}
					items={chartTypesFor(selectedMetric).map((t) => ({
						label: chartTypeLabel(t),
						selected: t === selectedChartType,
						onClick: onSelectChartType ? () => onSelectChartType(t) : undefined
					}))}
				/>
			)}
		</AppIconButtonGroup>
	)
}
